import React, { useEffect, useMemo, useState } from "react";
import { DiseaseCategoryType, UserRoles } from "fe-common/graphql";
import Tabs from "../../../themeComponents/tabs";
import { DiseaseCluster } from "fe-common/hooks/sortFeaturesWithCrs";
import Overview from "./overview";
import MiniDiseasePlan from "./miniDiseasePlan";
import { References } from "./references";
import MiniDiseaseGraph from "./miniDiseaseGraph";
import { SubTypes } from "../SubTypes";
import { DDClinicalReasoning } from "fe-common/graphql/dao";
import styled from "@mui/material/styles/styled";
import { Theme } from "@mui/material/styles";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import {
  MobileView,
  NavigationPanelBreakPoint,
} from "../../../util/aKaunGeneralUtil";
import { StrengthCircularIndicator } from "../common/strengthCircularIndicator";
import { LikeDislikeDisease } from "../common/likeDislikeDisease";
import { RosebudMobileBottomNavigationHeight } from "../common/rosebudButtomNavigationButton";
import { useDiseasesSections } from "../hooks/useDiseasesSections";
import { useGetUserRoles } from "fe-web-components/roles/useGetUserRoles";

//region [[ Styles ]]
const HeaderWrapper = styled("div")(({ theme }: { theme?: Theme }) => ({
  padding: "6px 16px",
  minHeight: "65px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  flexFlow: "column-reverse",

  [theme.breakpoints.down(MobileView)]: {
    minHeight: "145px",
    backgroundColor: KahunColorMap.Secondary,
  },
  marginTop: "-7px",
  backgroundColor: "white",
}));

const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  backgroundColor: "white",
  justifyContent: "space-between",
  padding: "18px 20px 0 20px",
  paddingRight: "20px",
  paddingLeft: "20px",
  [theme.breakpoints.down(MobileView)]: {
    paddingBottom: `${RosebudMobileBottomNavigationHeight}px`,
  },
}));

const MiddleMenu = styled("div")(({ theme }: { theme?: Theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "10px",
  [theme.breakpoints.down(MobileView)]: {
    marginBottom: "10px",
  },
}));

const ContentContainer = styled("div")(({ theme }: { theme?: Theme }) => ({
  flex: "auto",
  overflow: "auto",
  backgroundColor: "white",
  minHeight: "300px",
  borderRadius: "12px",
  [theme.breakpoints.up(NavigationPanelBreakPoint)]: {
    maxHeight: "450px",
  },
}));

//endregion [[ Styles ]]

//region [[ Functions ]]
interface MiniDiseaseTab {
  name: string;
  component: any;
  componentProps: {};
  filter?: (roles, selectedCR) => boolean;
}
const miniDiseaseTabs: MiniDiseaseTab[] = [
  {
    name: "Overview",
    component: Overview,
    componentProps: {},
    filter: (roles, selectedCR) => {
      return (
        roles.includes(UserRoles.KahunBackofficeUsers) ||
        roles.includes(UserRoles.KahunPatientDemoUsers) ||
        selectedCR.disease[0]?.feature?.description?.text
      );
    },
  },
  {
    name: "History And Physical",
    component: MiniDiseasePlan,
    componentProps: {
      name: "History And Physical",
      category: DiseaseCategoryType.OutcomeSigns,
    },
  },
  {
    name: "Workup",
    component: MiniDiseasePlan,
    componentProps: {
      name: "Suggested lab workup",
      category: DiseaseCategoryType.OutcomeTest,
    },
  },
  {
    name: "References",
    component: References,
    componentProps: {},
    filter: (roles) =>
      roles.includes(UserRoles.KahunBackofficeUsers) ||
      roles.includes(UserRoles.KahunPatientDemoUsers),
  },
  {
    name: "Graph",
    component: MiniDiseaseGraph,
    componentProps: {},
    filter: (roles) =>
      roles.includes(UserRoles.KahunBackofficeUsers) ||
      roles.includes(UserRoles.KahunPatientDemoUsers),
  },
];
//endregion [[ Functions ]]

//region [[ Props ]]
interface MiniDiseaseViewProps {
  fWithCrs: DiseaseCluster;
  selectedSubType?: DDClinicalReasoning;
}
//endregion [[ Props ]]

const MiniDiseaseView = ({
  fWithCrs,
  selectedSubType,
}: MiniDiseaseViewProps) => {
  const [selectedCR, setSelectedCR] = useState<DDClinicalReasoning>(
    selectedSubType,
  );

  const { crs } = useDiseasesSections();

  const { roles, loading } = useGetUserRoles();

  useEffect(() => {
    setSelectedCR(selectedSubType);
  }, [selectedSubType]);

  const tabs = useMemo(() => {
    return miniDiseaseTabs.filter(
      (t) => !t.filter || t.filter(roles, selectedCR || fWithCrs.crs[0]),
    );
  }, [roles, selectedCR, fWithCrs]);

  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const fCrs = fWithCrs?.crs;
  const selectedTab = tabs[selectedIndex];
  const Content = selectedTab.component;

  if (loading) return null;

  return (
    <>
      <HeaderWrapper>
        <SubTypes
          crs={fCrs}
          filteredId={fWithCrs?.qf?.id}
          selectedCR={selectedCR}
          setSelectedCR={setSelectedCR}
        />

        <MiddleMenu>
          <StrengthCircularIndicator strength={fWithCrs.normalizedStrength} />

          <LikeDislikeDisease disease={fWithCrs?.qf} />
        </MiddleMenu>

        <Tabs
          items={tabs}
          selectedIndex={selectedIndex}
          clickHandler={setSelectedIndex}
        />
      </HeaderWrapper>

      <ContentWrapper>
        <ContentContainer>
          <Content
            fWithCrs={fWithCrs}
            selectedCR={selectedCR}
            changeTab={setSelectedIndex}
            crs={crs}
            {...selectedTab.componentProps}
          />
        </ContentContainer>
      </ContentWrapper>
    </>
  );
};

export default MiniDiseaseView;
